import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { Co2EmissionsDashboardDto } from 'api/dashboard/dtos/Co2EmissionsDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import useInfiniteReveal from '../components/useInfiniteReveal';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import openFuelCharges from '../utils/openFuelCharges';
import styles from './Co2Emissions.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a',
	'#038cb6',
	'#02c7c7',
	'#1dd3d3',
	'#45dcdc',
	'#6de0e0',
];

const formatCo2 = (value: number) =>
	value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kg';

const Co2Emissions = () => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<Co2EmissionsDashboardDto[] | null>(null);
	const [previousTotal, setPreviousTotal] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('co2Emissions');

	const fuelTypeLabel = () => t('common.generic_types.FUEL' as any) as string;

	const handleRowClick = (item: Co2EmissionsDashboardDto) => {
		openFuelCharges({
			dateFrom,
			dateTo,
			fuelTypeLabel: fuelTypeLabel(),
			vehicle: { id: item.vehicleId, name: item.registrationNumber },
		});
	};

	const goToFuelCharges = () => {
		openFuelCharges({ dateFrom, dateTo, fuelTypeLabel: fuelTypeLabel() });
	};

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			setPreviousTotal(null);
			setPreviousRange(null);
			try {
				const current = await DashboardService.getCo2Emissions({ dateFrom, dateTo });
				if (!cancelled) setData(current);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const previous = await DashboardService.getCo2Emissions({ dateFrom: range.prevFrom, dateTo: range.prevTo });
				if (!cancelled) {
					setPreviousTotal((previous || []).reduce((s, x) => s + x.co2Kg, 0));
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousTotal(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [dateFrom, dateTo]);

	const items = data || [];
	const maxCo2 = Math.max(...items.map(d => d.co2Kg), 0);
	const grandTotal = items.reduce((sum, it) => sum + it.co2Kg, 0);
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	const buildVehicleTooltip = (item: Co2EmissionsDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.co2_emissions') as string}
			subTitleTooltip={t('dashboard.co2_emissions_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : !data || data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={formatCo2(grandTotal)}
						subLabel={t('dashboard.co2_total_flotte' as any) as string}
						currentTotal={grandTotal}
						previousTotal={previousTotal}
						previousRange={previousRange}
						onClick={goToFuelCharges}
					/>
					<div className={styles.list}>
						{visibleItems.map((item, index) => {
							const barWidth = maxCo2 > 0 ? (item.co2Kg / maxCo2) * 100 : 0;
							const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
							return (
								<div
									key={item.vehicleId}
									className={styles.row}
									onClick={() => handleRowClick(item)}
								>
									<span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="co2-emissions-tooltip" data-place="right">{item.registrationNumber}</span></span>
									<div className={styles.barTrack}>
										<div className={styles.barFill} style={{ width: `${barWidth}%`, background: color }} />
									</div>
									<span className={styles.cost}>
										{formatCo2(item.co2Kg)}
									</span>
								</div>
							);
						})}
						{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
					<ReactTooltip id="co2-emissions-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default Co2Emissions;
